import { Theme } from '@rneui/themed';
import { ThemeConsumer } from '@rneui/themed';
import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity, ScrollView, useWindowDimensions } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ZenIcon, ZenText, ZenToast } from 'zen-core-elements';
import PaymentMethods from './paymentMethods';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import CommonService from '../../../../shared/services/commonService';
import { sellScreenPaymentInitialStateType } from '../../../../redux/types/sellScreenTypes';
import { removeTransactionData } from '../../../../redux/slices/newSellscreenPaymentSlice';
import { appColors } from '../../../../themes/appTheme';
import Popover from 'react-native-popover-view';
import CommonDialogBox from '../../../../shared/commonComponents/commonDialogBox';


const SplitPayment = () => {
	const dispatch = useAppDispatch()
	const { width } = useWindowDimensions()
	const { totalAmount, remainingAmount, paidAmount, transactionData } = useAppSelector((state) => state?.NewSellscreenPaymentSlice)
	const { customerDetails } = useAppSelector((state) => state?.NewSellscreenSlice)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const [transactionDeletePopover, setTransactionDeletePopover] = useState<{ isVisible: boolean, id: string | null }>({ isVisible: false, id: null })


	const transactionDeleteAlertDialogProps = {
		content: "Are you sure you want to delete this transaction?",
		button: [{ id: 1, name: "Yes" }, { id: 2, name: "No" }],
		icon: { name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community' }
	}

	const [modal, setModal] = useState(false);
	const [editPayment, setEditPayment] = useState<sellScreenPaymentInitialStateType["transactionData"][0]>()

	const addPaymentRow = () => {
		setModal(true)
	};

	const removeTransaction = (id: string) => {
		setTransactionDeletePopover({ isVisible: true, id })
	};

	const setModalClose = () => {
		setModal(false);
		setEditPayment(undefined)
	}

	const openEditModal = (payment: sellScreenPaymentInitialStateType["transactionData"][0]) => {
		if (customerDetails?.id) {
			setModal(true)
			setEditPayment(payment)
		} else {
			ZenToast({ type: "warning", message: "Please choose a customer to continue with the payment." })
		}
	}

	const transactionDeleteConfirmation = (item: { id: number }) => {
		if (item?.id == 1) {
			dispatch(removeTransactionData(transactionDeletePopover?.id!!))
		}
		setTransactionDeletePopover({ isVisible: false, id: null })
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<SafeAreaView style={styles.container}>
						<ScrollView>
							<View style={styles.mathBox}>
								<View style={styles.mathRow}>
									<ZenText content="Total Covered" style={styles.mathLabel} />
									<ZenText content={CommonService?.currencyStandard(currencySymbol, paidAmount?.toString())} style={styles.mathValue} />
								</View>
								<View style={styles.mathRow}>
									<ZenText content="Remaining" style={styles.mathLabel} />
									<ZenText content={remainingAmount === 0 ? 'Fully Paid' : CommonService?.currencyStandard(currencySymbol, remainingAmount?.toString())} style={[styles.mathValue, { color: remainingAmount === 0 ? theme?.green : theme?.red500 }]} />
								</View>
							</View>
							{/* Standardized List Item */}
							<View style={{ gap: 16, marginTop: 24 }}>
								{transactionData?.map((payment, index) => (
									<View key={payment?._id} style={styles.paymentRow}>
										<TouchableOpacity
											style={[styles.paymentSelector, payment?.paymentMode && { borderColor: theme?.primary, backgroundColor: theme?.tealPrimary + "90" }]}
											onPress={() => openEditModal(payment)}
										>
											<View style={styles.rowLeft}>
												<View style={[styles.indexCircle, payment?.paymentMode && { backgroundColor: theme?.primary }]}>
													<ZenText content={(index + 1)?.toString()} style={styles.indexText} />
												</View>
												<ZenText content={payment?._paymentMethod || 'Select Payment Method'} style={[styles.methodLabel, payment?.paymentMode && { color: theme?.primary, fontFamily: theme?.bold, fontSize: theme?.smallerFontSize }]} />
											</View>
											{payment?._paidAmount ? <ZenText content={CommonService?.currencyStandard(currencySymbol, payment?._paidAmount?.toString())} style={styles.rowAmount} /> : null}
										</TouchableOpacity>

										{/* <TouchableOpacity disabled={payment?._paymentMethod != "Cash"} onPress={() => removeRow(payment?._id!!)} style={[styles.deleteBtn, { opacity: payment?._paymentMethod == "Cash" ? 1 : 0.3 }]}>
											<ZenIcon name="close-circle" type="material-community" size={24} color={payment?.paymentMode ? theme?.red500 : theme?.lightGrey} />
										</TouchableOpacity> */}
										<TouchableOpacity onPress={() => removeTransaction(payment?._id!!)} style={styles.deleteBtn}>
											<ZenIcon name="close-circle" type="material-community" size={24} color={theme?.red500} />
										</TouchableOpacity>
									</View>
								))}
							</View>

							{remainingAmount > 0 ? <TouchableOpacity style={styles.addRowBtn} onPress={addPaymentRow}>
								<ZenIcon name="plus" type="material-community" size={20} color={theme?.primary} />
								<ZenText content="Add Payment Split" style={styles.addRowText} />
							</TouchableOpacity> : remainingAmount !== 0 ?
								<ZenText content={"GIVE CHANGE: " + CommonService?.currencyStandard(currencySymbol, (Math.abs(remainingAmount))?.toString())} style={[styles.addRowText, { textAlign: "center", paddingTop: 20 }]} /> : null}
						</ScrollView>

						<PaymentMethods
							modalOpen={modal}
							onClose={setModalClose}
							editPayment={editPayment}
						/>

						{/* For delete transaction confirmation */}
						<Popover isVisible={transactionDeletePopover?.isVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }} onRequestClose={() => { setTransactionDeletePopover({ isVisible: false, id: null }) }}>
							<CommonDialogBox type="OTHER" dialog={transactionDeleteAlertDialogProps} confirmation={transactionDeleteConfirmation} />
						</Popover>

					</SafeAreaView>
				)
			}}
		</ThemeConsumer>
	);
};

const style = (theme: Theme) => StyleSheet.create({
	container: { flex: 1 },

	paymentRow: { flexDirection: 'row', alignItems: 'center' },
	paymentSelector: { flex: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, backgroundColor: theme?.iceWhite, borderRadius: 20, borderWidth: 1.5, borderColor: theme?.softSlate },
	rowLeft: { flexDirection: 'row', alignItems: 'center' },
	indexCircle: { width: 20, height: 20, borderRadius: 12, backgroundColor: theme?.lightGrey, justifyContent: 'center', alignItems: 'center', marginRight: 12 },
	indexText: { color: theme?.light, fontSize: theme?.miniLabel, fontFamily: theme?.bold },
	methodLabel: { fontFamily: theme?.medium, fontSize: theme?.smallHeaderFontSize, color: theme?.grey },
	rowAmount: { fontFamily: theme?.bold, color: theme?.primary, fontSize: theme?.smallerFontSize },
	deleteBtn: { marginLeft: 12 },

	addRowBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 8 },
	addRowText: { color: theme?.primary, fontFamily: theme?.bold, fontSize: theme?.smallFontSize },

	mathBox: { marginTop: 12, padding: 20, backgroundColor: theme?.iceWhite, borderRadius: 16, borderStyle: 'dashed', borderWidth: 1, borderColor: theme?.grey },
	mathRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
	mathLabel: { fontSize: theme?.smallHeaderFontSize, color: theme?.grey, fontFamily: theme?.medium },
	mathValue: { fontSize: theme?.smallHeaderFontSize, fontFamily: theme?.bold, color: theme?.lightDark },
});

export default SplitPayment;